<script lang="ts">

    import Map from "ol/Map";
    import View from "ol/View";
    //import TileLayer from 'ol/layer/Tile';
    import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
    import VectorSource from "ol/source/Vector";
    //import XYZ from 'ol/source/XYZ';
    import { onMount, tick } from "svelte";

    import { Geometry, MultiPoint, Point } from "ol/geom.js";
    import OSM from "ol/source/OSM.js";
    import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style.js";
    import { getVectorContext } from "ol/render";
    import { get as getProj, fromLonLat } from "ol/proj";
    import Feature from "ol/Feature";
    import Overlay from "ol/Overlay";
    import { ColorSource } from "./colors";
    import type { W84Location } from "./natsviewersession";

    export let visible = false

    export const setVisible = async (val: boolean) => {
        visible = val
        await tick()
        map?.updateSize()
        map?.render()
    }

    export const setDisplayMap = async (val: boolean) => {
        displayMap = val
    }

    interface Marker {
        color: string
        feature: Feature<Point>
    }

    const vectorSource = new VectorSource({
        features: [],
    });

    const vectorLayer = new VectorLayer({
        source: vectorSource,
    });

    const colorSource = new ColorSource()

    export const createMarker = (name: string) => {
        let ret: Marker = {
            color: colorSource.nextWithOpacity(0.4),
            feature: new Feature({
                geometry: new Point(fromLonLat([0, 0])),
                name: name,
            })
        }
        ret.feature.setStyle(new Style({
            image: new CircleStyle({
                radius: 5,
                stroke: new Stroke({
                    color: '#fff'
                }),
                fill: new Fill({
                    color: ret.color,
                })
            }),
        }))
        vectorSource.addFeature(ret.feature)
        return ret
    }

    export const updateMarker = (marker: Marker, locationData: W84Location) => {
        marker.feature.setGeometry(new Point(fromLonLat([locationData.lon, locationData.lat])))
    }

    export const removeMarker = (marker: Marker) => {
        colorSource.releaseColor(marker.color)
        vectorSource.removeFeature(marker.feature)
    }

    export const centerOnMarker = (marker: Marker) => {
        if (mapView == null || map == null) {
            return
        }
        let geom = marker.feature.getGeometry()
        if (geom) {
            let maxZoom = 10.0
            let mapZoom = mapView.getZoom()
            if (mapZoom && mapZoom > maxZoom) {
                maxZoom = mapZoom
            }
            mapView.fit(geom, {maxZoom: maxZoom, duration: 700})
        }
    }


    let ddiv: HTMLElement;
    let draghdr: HTMLElement;
    let popupEl: HTMLElement;
    let overlayText = ""

    let map: Map | null = null;
    let mapView: View | null = null
    onMount(() => {
        const tileLayer = new TileLayer({
            source: new OSM(),
        });

        // Limit multi-world panning to one world east and west of the real world.
        // Geometry coordinates have to be within that range.
        const extent = getProj("EPSG:3857").getExtent().slice();
        extent[0] += extent[0];
        extent[2] += extent[2];

        mapView = new View({
            center: [0, 0],
            zoom: 2,
            extent,
        })
        map = new Map({
            target: "map",
            layers: [tileLayer, vectorLayer],
            view: mapView,
            controls: [],
        });

        var oldDragX = 0,
            oldDragY = 0;
        draghdr.onmousedown = dragMouseDown;

        function dragMouseDown(e: MouseEvent) {
            e = e || window.event;
            e.preventDefault();
            // get the mouse cursor position at startup:
            oldDragX = e.clientX;
            oldDragY = e.clientY;
            document.onmouseup = closeDragElement;
            // call a function whenever the cursor moves:
            document.onmousemove = elementDrag;
        }

        function elementDrag(e: MouseEvent) {
            e = e || window.event;
            e.preventDefault();
            // calculate the new cursor position:
            let posLeft = oldDragX - e.clientX;
            let posTop = oldDragY - e.clientY;
            oldDragX = e.clientX;
            oldDragY = e.clientY;
            // set the element's new position:
            ddiv.style.top = ddiv.offsetTop - posTop + "px";
            ddiv.style.left = ddiv.offsetLeft - posLeft + "px";
        }

        function closeDragElement() {
            // stop moving when mouse button is released:
            document.onmouseup = null;
            document.onmousemove = null;
        }

        map.render();

        const popup = new Overlay({
            element: popupEl,
            positioning: "bottom-center",
            stopEvent: false,
        });
        map.addOverlay(popup);

        // display popup on click
        map.on("click", function (evt) {
            if (map == null) {
                return;
            }
            const feature = map.forEachFeatureAtPixel(
                evt.pixel,
                function (feature) {
                    return feature;
                }
            ) as Feature<Geometry>;
            if (feature) {
                let pos = (feature.getGeometry() as Point).getCoordinates();
                popup.setPosition(pos);
                hidePopup = false;
                overlayText = feature.get('name')
            } else {
                hidePopup = true;
            }
        });
    });

    let mapResize = () => {
        if (map != null) {
            map.updateSize();
        }
    };
    let displayMap = true;
    async function toggleMap() {
        displayMap = !displayMap;
        await tick();
        mapResize();
    }

    let hidePopup = true;
</script>

<div class="draggable" bind:this={ddiv} on:mouseup={mapResize} class:hidden={!visible}>
    <div class="draghdr" bind:this={draghdr}>
        Area map <button class="btn-small" on:click={toggleMap}>Toggle</button>
    </div>
    <div id="map" class:hidden={!displayMap} on:resize={mapResize}>
        <div class="popup" bind:this={popupEl} class:hidden={hidePopup}>
            {overlayText}
        </div>
    </div>
</div>

<style>
    .popup {
        background-color: #00000030;
        border-radius: 0.5em;
        padding-left: 0.5em;
        padding-right: 0.5em;
        border: 1px solid #00000030;
    }
    .hidden {
        display: none;
    }
    .btn-small {
        padding-top: 0;
        padding-bottom: 0;
    }
    .draggable {
        position: absolute;
        z-index: 9;
        background-color: #f1f1f1;
        border: 1px solid #d3d3d3;
        text-align: center;
    }
    .draghdr {
        padding-right: 3em;
        padding-left: 3em;
        cursor: grab;
    }

    #map {
        border: 2px solid;
        padding-bottom: 20px;
        width: 300px;
        height: 300px;
        resize: both;
        overflow: auto;
    }
</style>
